import {
    AddressDto,
    Provider,
    SubmitLoanApiRequest,
    ResidenceOwnership,
    IncomeSource,
    UserContextDto,
    ExtendedProperties,
    EmploymentStatus,
    LoanApplicationSubmitSource,
} from '@servicetitan/financing-api-client';
import { ApplicationData } from './ApplicationDataStore';
import { calculateIncomes } from '../../utils/calculateIncomes';

export interface PrepareQualificationDataParams {
    applicationData: ApplicationData;
    tenantId: string;
    providerName: Provider;
    buId: string;
    technicianId?: string;
    jobId: string | null;
    customerId?: string;
    userContext?: UserContextDto;
    previousLoanId?: string;
    industryIdFromMapping?: string;
    mappingSource?: string;
    sourceImprovement?: string;
    hasMailingAddressStep?: boolean;
}

// Map string residence type to ResidenceOwnership enum
const mapResidenceOwnership = (residenceType: string | undefined): ResidenceOwnership => {
    if (!residenceType) {
        return ResidenceOwnership.Own; // Default to Own
    }

    switch (residenceType) {
        case 'own':
            return ResidenceOwnership.Own;
        case 'rent':
            return ResidenceOwnership.Rent;
        case 'ownWithMortgage':
            return ResidenceOwnership.OwnWithMortgage;
        case 'other':
            return ResidenceOwnership.Other;
        default:
            return ResidenceOwnership.NotSet;
    }
};

// Map income type string to IncomeSource enum, considering employment status for GoodLeap->Turns mapping
// FIN-16696: When GoodLeap employment status is NotEmployed/Retired/Other, use appropriate income source
const mapIncomeSource = (
    incomeType: string | undefined,
    employmentStatus?: EmploymentStatus,
): IncomeSource => {
    // Check employment status first - this overrides incomeType for GoodLeap->Turns mapping
    if (employmentStatus) {
        switch (employmentStatus) {
            case EmploymentStatus.NotEmployed:
                return IncomeSource.Other;
            case EmploymentStatus.Retired:
                return IncomeSource.SocialSecurity;
            case EmploymentStatus.Other:
                return IncomeSource.Other;
            // EmploymentStatus.Employed and NotSet fall through to incomeType mapping
        }
    }

    if (!incomeType) {
        return IncomeSource.JobIncome; // Default to JobIncome
    }

    // Map the income type values from incomeTypes object to IncomeSource enum
    switch (incomeType) {
        case 'Job Income':
            return IncomeSource.JobIncome;
        case 'Self Employed':
            return IncomeSource.SelfEmployed;
        case 'Benefits':
            return IncomeSource.Benefits;
        case 'Pension':
            return IncomeSource.Pension;
        case 'Social Security':
            return IncomeSource.SocialSecurity;
        case 'Disability':
            return IncomeSource.Disability;
        case 'Military':
            return IncomeSource.Military;
        case 'Student':
            return IncomeSource.Student;
        case 'Other':
            return IncomeSource.Other;
        default:
            return IncomeSource.NotSet; // Default to JobIncome
    }
};

// Format phone number to remove special characters
const formatPhoneNumber = (phone: string | undefined): string | undefined => {
    if (!phone) {
        return undefined;
    }
    return phone.replace(/[\(\)\s-]/g, '');
};

// Format SSN to remove non-digit characters
const sanitizeSSN = (ssn: string | undefined): string | undefined => {
    if (!ssn) {
        return undefined;
    }
    return ssn.replace(/\D/g, '');
};

export const prepareQualificationData = ({
    applicationData,
    tenantId,
    providerName,
    buId,
    technicianId,
    jobId,
    customerId,
    userContext,
    previousLoanId,
    industryIdFromMapping,
    mappingSource,
    sourceImprovement,
    hasMailingAddressStep,
}: PrepareQualificationDataParams): SubmitLoanApiRequest => {
    const {
        applicant: {
            primaryApplicant: {
                firstName,
                lastName,
                email,
                primaryPhone: phone,
                dateOfBirth,
                ssn,
                annualIncome,
                employment: {
                    grossMonthlyIncome: monthlyIncome,
                    incomeType,
                    status: employmentStatus,
                } = {},
                jobDetails: { industryType } = {},
                requestedLoanAmount,
            } = {},
        } = {},
    } = applicationData;

    const primaryJobDetails = applicationData.applicant.primaryApplicant?.jobDetails;
    const jobAddress = primaryJobDetails?.address;

    // Prefer a real mailing/home address over the job address; gated on the boolean so a
    // lingering override address left in the store after unchecking is ignored.
    const homeAddress = hasMailingAddressStep
        ? applicationData.mailingAddress.primaryApplicant?.address
        : primaryJobDetails?.mailingAddressDiffersFromJobAddress
          ? primaryJobDetails?.mailingAddress?.address
          : jobAddress;

    const residenceSource =
        primaryJobDetails?.residenceType ??
        applicationData.serviceProperty?.primaryApplicant?.housingType;

    // Use industryType from jobDetails if available, otherwise use the mapped industry ID
    const finalIndustryId = industryType ?? industryIdFromMapping;

    const { grossMonthlyIncome } = calculateIncomes({
        grossMonthlyIncome: monthlyIncome,
        annualIncome,
    });

    const extendedProperties: ExtendedProperties | undefined =
        mappingSource && sourceImprovement
            ? {
                  source: mappingSource,
                  sourceImprovement,
              }
            : finalIndustryId
              ? { industryId: finalIndustryId }
              : undefined;

    return {
        provider: providerName,
        tenantContext: {
            tenantId,
            buId,
        },
        jobContext: (() => {
            const effectiveJobId: string | null = jobId !== '' ? jobId : null;
            return (effectiveJobId ?? customerId)
                ? { jobId: effectiveJobId, customerId, technicianId }
                : undefined;
        })(),
        sourceContext: {
            source: applicationData.source as unknown as LoanApplicationSubmitSource,
            sourceId: previousLoanId,
            sourceFinancingId: previousLoanId,
        },
        userContext,
        applicant: {
            firstName,
            lastName,
            email,
            phone: formatPhoneNumber(phone),
            dob: dateOfBirth,
            ssn: sanitizeSSN(ssn),
            address: {
                street: homeAddress?.street,
                city: homeAddress?.city,
                state: homeAddress?.state,
                zip: homeAddress?.zip,
            } as AddressDto,
        },
        residenceOwnership: mapResidenceOwnership(residenceSource),
        incomeSource: mapIncomeSource(incomeType, employmentStatus as EmploymentStatus),
        grossMonthlyIncome,
        requestedAmount: requestedLoanAmount ?? 0, // Use requested loan amount from applicant data
        ...(extendedProperties ? { extendedProperties } : {}),
    };
};
